import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RefreshCw, AlertCircle, ImageIcon, Search, X, Filter, ChevronDown, CheckSquare, Square, MinusSquare, Tag, Eye, Award, Trash2, RotateCcw } from 'lucide-react';
import config from '@/config';
import { useAuth } from '@/contexts/AuthContext';

// Generic Dialog Component
function Dialog({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Pick which batch to create workflows for.
 *
 * Creating across every batch walks the whole work_record table, so any record
 * deliberately withdrawn from production gets a workflow again. Scoping to one
 * batch is therefore the default; "all batches" stays available but has to be
 * chosen deliberately.
 */
function CreateWorkflowsContent({ authenticatedFetch, onConfirm, onCancel }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/work/work-batches/list?page=1&page_size=200&sort_by=created_at&sort_order=desc`
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`);
        if (!cancelled) {
          const items = data.batches || [];
          setBatches(items);
          // Default to the newest batch rather than to "everything".
          if (items.length) setSelected(items[0].id);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticatedFetch]);

  const chosen = batches.find((b) => b.id === selected);
  const isAll = selected === '__all__';

  return (
    <div className="p-6 space-y-4">
      <p className="text-sm text-gray-600">
        Creates a workflow for every record in the chosen batch that has a brand, lenses,
        a ready frame, and a logo where one is needed. Records that already have a
        workflow are skipped.
      </p>

      {loading ? (
        <div className="py-8 flex items-center justify-center gap-2 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading batches…</span>
        </div>
      ) : error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-900">{error}</div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {batches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.batch_name || batch.id}
                  {batch.number_of_requested_output != null
                    ? ` — ${batch.number_of_requested_output} records`
                    : ''}
                  {batch.completed ? ' (completed)' : ''}
                </option>
              ))}
              <option value="__all__">⚠ All batches — every work record in the database</option>
            </select>
          </div>

          {isAll ? (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded text-sm text-amber-900">
              This walks every work record, including any withdrawn from production. Unless
              you specifically want that, pick a single batch.
            </div>
          ) : chosen?.completed ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
              This batch is marked completed. Creating workflows will start work on it again.
            </div>
          ) : null}
        </>
      )}

      <div className="flex gap-3 pt-2">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(isAll ? null : selected, chosen?.batch_name || 'selected batch')}
          disabled={loading || Boolean(error) || !selected}
          className={`flex-1 px-4 py-2 rounded-lg text-white disabled:opacity-40 ${
            isAll ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          {isAll ? 'Create for ALL batches' : 'Create workflows'}
        </button>
      </div>
    </div>
  );
}

// Bulk Delete Dialog Content
function BulkDeleteContent({ selectedCount, onConfirm, onCancel }) {
  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-red-100 rounded-full">
            <Trash2 className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Work Records</h3>
            <p className="text-sm text-gray-600">This action cannot be undone</p>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">
            You are about to permanently delete <strong>{selectedCount}</strong> work record{selectedCount !== 1 ? 's' : ''}.
          </p>
          <p className="text-sm text-red-700 mt-2">
            This will remove all associated data and cannot be recovered.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Delete Records
        </button>
      </div>
    </div>
  );
}




function BulkResetContent({ selectedCount, selectedRecords, records, onConfirm, onCancel }) {
  const [resetType, setResetType] = useState('workflow');
  const [resetGlassesReview, setResetGlassesReview] = useState(false);
  const [resetAllWorkflows, setResetAllWorkflows] = useState(false);
  
  // Check if any selected records have frame images
  const hasFrameImages = records ? records.some(r => r.glasses_frame_image_id) : false;
  const canResetWorkRecord = !hasFrameImages;
  
  const handleConfirm = () => {
    const resetOptions = {
      type: resetType,
      resetGlassesReview: resetType === 'workflow' ? resetGlassesReview : false,
      resetAllWorkflows,
      recordIds: Array.from(selectedRecords)
    };
    onConfirm(resetOptions);
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-yellow-100 rounded-lg">
            <RotateCcw className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Reset Processing</h3>
            <p className="text-sm text-gray-600">
              {selectedCount} record{selectedCount !== 1 ? 's' : ''} selected
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="px-6 border-b border-gray-200">
        <div className="flex gap-1">
          <button
            onClick={() => setResetType('workflow')}
            className={`px-4 py-3 text-sm font-medium transition-all relative ${
              resetType === 'workflow'
                ? 'text-yellow-700 bg-yellow-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Workflow Processing Reset
            {resetType === 'workflow' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600"></div>
            )}
          </button>
          
          <button
            onClick={() => canResetWorkRecord && setResetType('work_record')}
            disabled={!canResetWorkRecord}
            className={`px-4 py-3 text-sm font-medium transition-all relative ${
              !canResetWorkRecord
                ? 'text-gray-400 cursor-not-allowed opacity-50'
                : resetType === 'work_record'
                ? 'text-yellow-700 bg-yellow-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Work Record Data Reset
            {resetType === 'work_record' && canResetWorkRecord && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600"></div>
            )}
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {/* Workflow Processing Tab */}
        {resetType === 'workflow' && (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-gray-700 mb-4">
                Reset workflow processing for glasses frames and associated workflows
              </p>
              
              {/* Reset Glasses Review Badge */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 block mb-3">
                  Options
                </label>
                <button
                  onClick={() => setResetGlassesReview(!resetGlassesReview)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all ${
                    resetGlassesReview
                      ? 'border-yellow-500 bg-yellow-50 text-yellow-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    resetGlassesReview ? 'border-yellow-600 bg-yellow-600' : 'border-gray-300'
                  }`}>
                    {resetGlassesReview && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="font-medium">Reset Glasses Review</span>
                </button>
                <p className="text-xs text-gray-600 mt-2 ml-1">
                  Clear the glasses review status for all frames
                </p>
              </div>

              {/* Workflow Scope Switch */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-3">
                  Workflow Scope
                </label>
                <div className="bg-gray-50 rounded-lg p-1 border border-gray-200 inline-flex">
                  <button
                    onClick={() => setResetAllWorkflows(false)}
                    className={`px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                      !resetAllWorkflows
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Current Workflows
                  </button>
                  <button
                    onClick={() => setResetAllWorkflows(true)}
                    className={`px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                      resetAllWorkflows
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    All Workflows (by UPC)
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2 ml-1">
                  {resetAllWorkflows 
                    ? 'Reset all workflows associated with the selected UPCs' 
                    : 'Reset only the currently active workflows'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Work Record Data Reset Tab */}
        {resetType === 'work_record' && (
          <div className="space-y-6">
            {!canResetWorkRecord ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Cannot Reset Work Record Data</p>
                  <p className="text-sm text-red-700 mt-1">
                    Some selected records have frame images attached. Remove frame images first to enable this option.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <p className="text-sm text-gray-700 mb-4">
                    Reset work record assignments and bindings
                  </p>
                  
                  {/* What will be cleared */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <p className="text-sm font-medium text-yellow-900 mb-2">This will clear:</p>
                    <ul className="space-y-1.5">
                      <li className="text-sm text-yellow-800 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-600"></div>
                        Brand assignments
                      </li>
                      <li className="text-sm text-yellow-800 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-600"></div>
                        Lenses assignments
                      </li>
                      <li className="text-sm text-yellow-800 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-600"></div>
                        Logo assignments
                      </li>
                      <li className="text-sm text-yellow-800 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-600"></div>
                        Frame bindings
                      </li>
                      <li className="text-sm text-yellow-800 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-600"></div>
                        Final image references
                      </li>
                    </ul>
                  </div>

                  {/* Workflow Scope Switch */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-3">
                      Workflow Scope
                    </label>
                    <div className="bg-gray-50 rounded-lg p-1 border border-gray-200 inline-flex">
                      <button
                        onClick={() => setResetAllWorkflows(false)}
                        className={`px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                          !resetAllWorkflows
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Current Workflows
                      </button>
                      <button
                        onClick={() => setResetAllWorkflows(true)}
                        className={`px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                          resetAllWorkflows
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        All Workflows (by UPC)
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 ml-1">
                      {resetAllWorkflows 
                        ? 'Reset all workflows associated with the selected UPCs' 
                        : 'Reset only the currently active workflows'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-5 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={resetType === 'work_record' && !canResetWorkRecord}
            className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Reset
          </button>
        </div>
      </div>
    </div>
  );
}




// Assignment Dialog Component
// ---------------------------------------------------------------------------
// Fuzzy matching for the assignment dialogs.
//
// Group keys come from work_record (lens_color, brand, logo_info) and are typed
// by hand, so they rarely equal the catalogue value exactly. What actually
// shows up in the data: case and spacing ("grey"/"Grey", "24k Iridium" vs the
// "24kiridium" identifier), a "Prizm " prefix the catalogue omits, the word
// "with" ("Copper with Silver mirror" -> "CopperSilverMirror"), abbreviations
// ("EmeraldIridium" -> "EmrldIridium"), qualifier suffixes ("Fire" ->
// "FireIridium") and outright typos ("Greey" -> "Grey"). Matching that by hand,
// one group at a time, is what this replaces.
// ---------------------------------------------------------------------------

// Auto-select the top candidate only when it scores this well AND beats the
// runner-up by the margin. Two equally-good candidates are left for a human --
// that is the point of the margin, not a limitation.
const AUTO_MATCH_SCORE = 0.80;
const AUTO_MATCH_MARGIN = 0.05;
// Below this a candidate is not worth offering as a suggestion.
const SUGGEST_SCORE = 0.45;

const NOISE_WORDS = /\b(?:with|and|the|lens|lenses|color|colour)\b/g;

const MATCH_SYNONYMS = [
  [/\bgray\b/g, 'grey'],
  [/\bgreey\b/g, 'grey'],
  [/\bemrld\b/g, 'emerald'],
  [/\bpolar(?!ized)\b/g, 'mirror'],
];

// Tokens that qualify a tint rather than identify it. Dropping one is allowed
// but costs a little score, so an exact match always outranks a stripped one.
const OPTIONAL_TOKENS = ['prizm', 'iridium', 'silver', 'mirror', 'polarized'];

function normalizeForMatch(value) {
  if (!value) return '';
  let text = String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // EmrldIridium -> Emrld Iridium
    .toLowerCase()
    .replace(/[_\-/.]+/g, ' ');
  text = text.replace(NOISE_WORDS, ' ');
  MATCH_SYNONYMS.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });
  return text.replace(/[^a-z0-9]/g, '');
}

// The string plus every useful "qualifier removed" form, each with the score
// multiplier that form is worth.
function matchVariants(normalized) {
  const variants = new Map();
  if (!normalized) return variants;
  variants.set(normalized, 1);
  OPTIONAL_TOKENS.forEach(token => {
    if (!normalized.includes(token)) return;
    const stripped = normalized.split(token).join('');
    if (stripped.length >= 3 && !variants.has(stripped)) variants.set(stripped, 0.92);
  });
  let bare = normalized;
  OPTIONAL_TOKENS.forEach(token => { bare = bare.split(token).join(''); });
  if (bare.length >= 3 && !variants.has(bare)) variants.set(bare, 0.88);
  return variants;
}

function bigrams(text) {
  const grams = [];
  for (let i = 0; i < text.length - 1; i += 1) grams.push(text.slice(i, i + 2));
  return grams;
}

// Sorensen-Dice on character bigrams: tolerant of typos and of the catalogue
// running words together, which plain equality is not.
function diceCoefficient(left, right) {
  if (left === right) return 1;
  if (left.length < 2 || right.length < 2) return 0;
  const leftGrams = bigrams(left);
  const rightGrams = bigrams(right);
  const pool = new Map();
  leftGrams.forEach(gram => pool.set(gram, (pool.get(gram) || 0) + 1));
  let shared = 0;
  rightGrams.forEach(gram => {
    const remaining = pool.get(gram) || 0;
    if (remaining > 0) {
      pool.set(gram, remaining - 1);
      shared += 1;
    }
  });
  return (2 * shared) / (leftGrams.length + rightGrams.length);
}

function rawScore(left, right) {
  if (!left || !right) return 0;
  if (left === right) return 1;
  const short = left.length <= right.length ? left : right;
  const long = left.length <= right.length ? right : left;
  if (short.length >= 3 && long.includes(short)) {
    const ratio = short.length / long.length;
    // A prefix is far stronger evidence than a match buried in the middle --
    // "Torch" belongs to TorchIridium, not Trailtorch.
    return long.startsWith(short) ? 0.88 + 0.10 * ratio : 0.72 + 0.12 * ratio;
  }
  return diceCoefficient(left, right);
}

function candidateFields(item) {
  return [item.colour, item.name, item.identifier].filter(Boolean);
}

// Rank every catalogue item against one group key, best first.
function rankItems(groupKey, items) {
  const queryVariants = matchVariants(normalizeForMatch(groupKey));
  const ranked = (items || []).map(item => {
    let score = 0;
    candidateFields(item).forEach(field => {
      matchVariants(normalizeForMatch(field)).forEach((fieldWeight, fieldValue) => {
        queryVariants.forEach((queryWeight, queryValue) => {
          const candidate = queryWeight * fieldWeight * rawScore(queryValue, fieldValue);
          if (candidate > score) score = candidate;
        });
      });
    });
    return { item, score };
  });
  ranked.sort((a, b) => b.score - a.score);
  return ranked;
}

function itemDisplayName(item) {
  return item.name || item.colour || item.identifier || 'Untitled';
}

function AssignmentDialog({ isOpen, onClose, type, data, onAssign, onCreateNew }) {
  const [assignments, setAssignments] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(null);
  const [createFormData, setCreateFormData] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [autoMatched, setAutoMatched] = useState({});
  const [optionFilters, setOptionFilters] = useState({});
  const autoAppliedRef = useRef(false);

  const { grouped_records, available_items, summary } = data || {};
  const groupKeys = useMemo(() => Object.keys(grouped_records || {}), [grouped_records]);

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Score every catalogue item against every group once per dataset, not per
  // render -- with ~50 lenses and ~30 groups this is 1,500 comparisons.
  const rankedByGroup = useMemo(() => {
    const ranked = {};
    if (!available_items?.length) return ranked;
    groupKeys.forEach(groupKey => {
      ranked[groupKey] = rankItems(groupKey, available_items);
    });
    return ranked;
  }, [groupKeys, available_items]);

  // Apply the confident matches once, when the dialog opens. Anything the user
  // has already touched is preserved.
  useEffect(() => {
    if (!isOpen) {
      autoAppliedRef.current = false;
      return;
    }
    if (autoAppliedRef.current || !available_items?.length) return;
    autoAppliedRef.current = true;

    const picks = {};
    groupKeys.forEach(groupKey => {
      const [best, runnerUp] = rankedByGroup[groupKey] || [];
      if (!best || best.score < AUTO_MATCH_SCORE) return;
      if (runnerUp && best.score - runnerUp.score < AUTO_MATCH_MARGIN) return;
      picks[groupKey] = best.item.id;
    });

    if (Object.keys(picks).length > 0) {
      setAssignments(prev => ({ ...picks, ...prev }));
      setAutoMatched(picks);
    }
  }, [isOpen, available_items, groupKeys, rankedByGroup]);

  const autoMatchedCount = useMemo(
    () => Object.keys(autoMatched).filter(key => assignments[key] === autoMatched[key]).length,
    [autoMatched, assignments]
  );

  const clearAutoMatches = () => {
    setAssignments(prev => {
      const next = { ...prev };
      Object.keys(autoMatched).forEach(key => {
        if (next[key] === autoMatched[key]) delete next[key];
      });
      return next;
    });
    setAutoMatched({});
  };

  if (!isOpen || !data) return null;

  const handleAssignment = (groupKey, itemId) => {
    setAssignments(prev => ({
      ...prev,
      [groupKey]: itemId
    }));
  };

  // Auto-generate identifier from name (kebab-case)
  const generateIdentifier = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleCreateFormChange = (field, value) => {
    const updates = { [field]: value };
    
    // Auto-generate identifier when name changes for brand
    if (field === 'name' && type === 'brand') {
      updates.identifier = generateIdentifier(value);
    }
    
    // Auto-generate identifier when colour changes for lenses
    if (field === 'colour' && type === 'lenses') {
      updates.identifier = generateIdentifier(value);
    }
    
    setCreateFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  const handleConfirm = () => {
    onAssign(assignments);
    setAssignments({});
    setExpandedGroups({});
    onClose();
  };

  const handleCreateNew = async (groupKey) => {
    const result = await onCreateNew(createFormData, groupKey);
    if (result) {
      setAssignments(prev => ({
        ...prev,
        [groupKey]: result.id
      }));
      setShowCreateForm(null);
      setCreateFormData({});
    }
  };

  const getTypeConfig = () => {
    switch (type) {
      case 'brand':
        return {
          title: 'Assign Brands',
          icon: <Tag className="w-5 h-5" />,
          groupLabel: 'Brand Name',
          itemLabel: 'Available Brands',
          selectPlaceholder: 'Select a brand...',
          createFields: [
            { name: 'name', label: 'Brand Name', required: true },
            { name: 'identifier', label: 'Identifier (slug)', required: false },
            { name: 'description', label: 'Description', required: false, textarea: true }
          ]
        };
      case 'lenses':
        return {
          title: 'Assign Lenses',
          icon: <Eye className="w-5 h-5" />,
          groupLabel: 'Lens Color',
          itemLabel: 'Available Lenses',
          selectPlaceholder: 'Select lenses...',
          createFields: [
            { name: 'identifier', label: 'Identifier', required: true },
            { name: 'colour', label: 'Color', required: true },
            { name: 'description', label: 'Description', required: false, textarea: true },
            { name: 'style', label: 'Style', required: false }
          ]
        };
      case 'logo':
        return {
          title: 'Assign Logos',
          icon: <Award className="w-5 h-5" />,
          groupLabel: 'Logo Info',
          itemLabel: 'Available Logos',
          selectPlaceholder: 'Select a logo...',
          createFields: [
            { name: 'identifier', label: 'Identifier', required: true },
            { name: 'description', label: 'Description', required: false, textarea: true },
            { name: 'logo_style', label: 'Logo Style', required: false }
          ]
        };
      default:
        return {};
    }
  };

  const config = getTypeConfig();
  const hasAssignments = Object.keys(assignments).length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-600 text-white rounded-lg">
                {config.icon}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{config.title}</h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  {summary?.total_unassigned_records} records in {groupKeys.length} groups
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          {autoMatchedCount > 0 && (
            <div className="mt-3 flex items-center justify-between gap-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-900">
                <span className="font-semibold">{autoMatchedCount}</span> of {groupKeys.length} groups
                {' '}matched automatically. Review the amber ones, then confirm.
              </p>
              <button
                onClick={clearAutoMatches}
                className="text-xs font-medium text-amber-800 hover:text-amber-950 underline whitespace-nowrap"
              >
                Clear auto-matches
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {groupKeys.map(groupKey => {
              const records = grouped_records[groupKey];
              const isCreating = showCreateForm === groupKey;
              const isExpanded = expandedGroups[groupKey];
              const isAssigned = !!assignments[groupKey];
              const ranked = rankedByGroup[groupKey] || [];
              const isAuto = isAssigned && autoMatched[groupKey] === assignments[groupKey];
              const topScore = ranked[0]?.score ?? 0;
              const assignedItem = available_items?.find(item => item.id === assignments[groupKey]);

              return (
                <div
                  key={groupKey}
                  className={`border rounded-lg overflow-hidden transition-all ${
                    isAuto
                      ? 'border-amber-300 bg-amber-50'
                      : isAssigned
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Collapsed Header */}
                  <div
                    className={`px-4 py-3 cursor-pointer hover:bg-opacity-80 transition-colors ${
                      isAuto ? 'bg-amber-100' : isAssigned ? 'bg-green-100' : 'bg-gray-50'
                    }`}
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <ChevronDown
                          className={`w-5 h-5 shrink-0 transition-transform ${
                            isExpanded ? 'transform rotate-180' : ''
                          } ${isAuto ? 'text-amber-700' : isAssigned ? 'text-green-700' : 'text-gray-500'}`}
                        />
                        <div className="min-w-0">
                          <h3 className={`font-semibold ${
                            isAuto ? 'text-amber-900' : isAssigned ? 'text-green-900' : 'text-gray-900'
                          }`}>
                            {groupKey}
                            {assignedItem && (
                              <span className={`font-normal ${isAuto ? 'text-amber-700' : 'text-green-700'}`}>
                                {' → '}{itemDisplayName(assignedItem)}
                              </span>
                            )}
                          </h3>
                          <p className={`text-sm ${
                            isAuto ? 'text-amber-700' : isAssigned ? 'text-green-700' : 'text-gray-600'
                          }`}>
                            {records.length} record{records.length !== 1 ? 's' : ''}
                            {!isAssigned && topScore >= SUGGEST_SCORE && (
                              <span className="text-gray-500">
                                {' · closest: '}{itemDisplayName(ranked[0].item)}
                                {' ('}{Math.round(topScore * 100)}%)
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isAuto ? (
                          <span className="px-3 py-1 bg-amber-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                            Auto {Math.round(topScore * 100)}%
                          </span>
                        ) : isAssigned ? (
                          <span className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-full flex items-center gap-1">
                            <CheckSquare className="w-3 h-3" />
                            Assigned
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">
                            Pending
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <>
                      {/* Assignment Section */}
                      <div className={`p-4 border-t ${
                        isAssigned ? 'border-green-200 bg-white' : 'border-gray-200 bg-white'
                      }`}>
                        {!isCreating ? (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {config.selectPlaceholder}
                              </label>
                              {(() => {
                                const filter = (optionFilters[groupKey] || '').trim().toLowerCase();
                                const visible = filter
                                  ? ranked.filter(({ item }) =>
                                      candidateFields(item).some(field =>
                                        field.toLowerCase().includes(filter)))
                                  : ranked;
                                const suggested = visible.filter(entry => entry.score >= SUGGEST_SCORE);
                                const rest = visible
                                  .filter(entry => entry.score < SUGGEST_SCORE)
                                  .sort((a, b) => itemDisplayName(a.item)
                                    .localeCompare(itemDisplayName(b.item)));
                                const renderOption = ({ item, score }, withScore) => (
                                  <option key={item.id} value={item.id}>
                                    {itemDisplayName(item)}
                                    {withScore ? ` — ${Math.round(score * 100)}%` : ''}
                                    {item.description ? ` · ${item.description}` : ''}
                                  </option>
                                );
                                return (
                                  <>
                                    <input
                                      type="text"
                                      value={optionFilters[groupKey] || ''}
                                      onChange={(e) => setOptionFilters(prev => ({
                                        ...prev,
                                        [groupKey]: e.target.value
                                      }))}
                                      placeholder={`Filter ${available_items?.length || 0} options…`}
                                      className="w-full mb-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                    <select
                                      value={assignments[groupKey] || ''}
                                      onChange={(e) => handleAssignment(groupKey, e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    >
                                      <option value="">-- Select --</option>
                                      {suggested.length > 0 && (
                                        <optgroup label="Suggested — best match first">
                                          {suggested.map(entry => renderOption(entry, true))}
                                        </optgroup>
                                      )}
                                      {rest.length > 0 && (
                                        <optgroup label={suggested.length > 0 ? 'Everything else' : 'All options'}>
                                          {rest.map(entry => renderOption(entry, false))}
                                        </optgroup>
                                      )}
                                    </select>
                                    {visible.length === 0 && (
                                      <p className="mt-2 text-sm text-gray-500">
                                        Nothing matches “{optionFilters[groupKey]}”.
                                      </p>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                            <button
                              onClick={() => {
                                setShowCreateForm(groupKey);
                                if (type === 'brand') {
                                  setCreateFormData({
                                    name: groupKey,
                                    identifier: generateIdentifier(groupKey)
                                  });
                                } else if (type === 'lenses') {
                                  setCreateFormData({
                                    colour: groupKey,
                                    identifier: generateIdentifier(groupKey)
                                  });
                                } else if (type === 'logo') {
                                  setCreateFormData({
                                    identifier: generateIdentifier(groupKey)
                                  });
                                }
                              }}
                              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              + Create new {type}
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <h4 className="font-medium text-gray-900">Create New {type}</h4>
                            {config.createFields.map(field => (
                              <div key={field.name}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {field.label} {field.required && <span className="text-red-500">*</span>}
                                </label>
                                {field.textarea ? (
                                  <textarea
                                    value={createFormData[field.name] || ''}
                                    onChange={(e) => handleCreateFormChange(field.name, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    rows={3}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={createFormData[field.name] || ''}
                                    onChange={(e) => handleCreateFormChange(field.name, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder={field.name === 'identifier' ? 'Auto-generated, can edit' : ''}
                                  />
                                )}
                              </div>
                            ))}
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleCreateNew(groupKey)}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                              >
                                Create & Assign
                              </button>
                              <button
                                onClick={() => {
                                  setShowCreateForm(null);
                                  setCreateFormData({});
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Records Preview */}
                      <div className="px-4 pb-4 bg-white">
                        <details className="mt-2">
                          <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                            View {records.length} records
                          </summary>
                          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                            {records.slice(0, 10).map(record => (
                              <div key={record.id} className="text-xs text-gray-600 pl-4">
                                • {record.upc} - {record.model || record.product || 'N/A'}
                              </div>
                            ))}
                            {records.length > 10 && (
                              <div className="text-xs text-gray-500 pl-4">
                                + {records.length - 10} more...
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {hasAssignments ? (
                <span>
                  {Object.keys(assignments).length} of {groupKeys.length} groups assigned
                </span>
              ) : (
                <span>No assignments yet</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!hasAssignments}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Assignments
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkRecordsTable() {
  const { authenticatedFetch } = useAuth();
  
  // Data state
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [availableBatches, setAvailableBatches] = useState([]);
  
  // Selection state
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  
  // Image preview state
  const [imagePreview, setImagePreview] = useState({
    isOpen: false,
    url: null,
    record: null
  });
  
  // Assignment dialog state
  const [assignmentDialog, setAssignmentDialog] = useState({
    type: null,
    isOpen: false,
    data: null
  });

  // Generic dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    content: null
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    upc: '',
    model: '',
    product: '',
    brand: '',
    lens_color: '',
    output_naming: '',
    needs_logo: null,
    work_batch_id: '',
    has_final_image_id: null,
    has_glasses_frame_id: null,
    has_glasses_frame_image_id: null
  });
  
  // Sort state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // UI state
  const [showFilters, setShowFilters] = useState(false);
  
  const observerTarget = useRef(null);
  const pageSize = 50;

  // Dialog handlers
  const openDialog = (title, content) => {
    setDialog({
      isOpen: true,
      title,
      content
    });
  };

  const closeDialog = () => {
    setDialog({
      isOpen: false,
      title: '',
      content: null
    });
  };

  // Bulk action handlers
  const handleBulkDelete = () => {
    const selectedRecordData = records.filter(r => selectedRecords.has(r.id));
    
    openDialog(
      'Delete Records',
      <BulkDeleteContent
        selectedCount={selectedRecords.size}
        selectedRecords={selectedRecords}
        records={selectedRecordData}
        onConfirm={async () => {
          try {
            const recordIds = Array.from(selectedRecords);
            console.log('Deleting records:', recordIds);
            
            // TODO: Implement actual API call
            // const response = await authenticatedFetch(
            //   `${config.BACKEND_API_ADDRESS}/work/work-record/bulk-delete`,
            //   {
            //     method: 'POST',
            //     headers: { 'Content-Type': 'application/json' },
            //     body: JSON.stringify({ record_ids: recordIds })
            //   }
            // );
            
            alert(`Deleting ${recordIds.length} records...`);
            
            closeDialog();
            setSelectedRecords(new Set());
            handleRefresh();
          } catch (err) {
            console.error('Error deleting records:', err);
            alert(`Failed to delete records: ${err.message}`);
          }
        }}
        onCancel={closeDialog}
      />
    );
  };

  const handleBulkReset = () => {
    const selectedRecordData = records.filter(r => selectedRecords.has(r.id));
    
    openDialog(
      'Reset Processing',
      <BulkResetContent
        selectedCount={selectedRecords.size}
        selectedRecords={selectedRecords}
        records={selectedRecordData}
        onConfirm={async (resetOptions) => {
          try {
            console.log('Reset options:', resetOptions);
            
            if (resetOptions.type === 'workflow') {
              // Call workflow processing reset endpoint
              const response = await authenticatedFetch(
                `${config.BACKEND_API_ADDRESS}/work/work-record/reset-workflow-processing`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    record_ids: resetOptions.recordIds,
                    reset_glasses_review: resetOptions.resetGlassesReview,
                    reset_all_workflows: resetOptions.resetAllWorkflows
                  })
                }
              );

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const data = await response.json();
              
              alert(
                `Workflow Processing Reset Complete!\n\n` +
                `Records processed: ${resetOptions.recordIds.length}\n` +
                `Glasses Review reset: ${resetOptions.resetGlassesReview ? 'Yes' : 'No'}\n` +
                `Workflow scope: ${resetOptions.resetAllWorkflows ? 'All workflows by UPC' : 'Current workflows only'}\n\n` +
                `${data.message || 'Success'}`
              );
              
            } else if (resetOptions.type === 'work_record') {
              // Call work record data reset endpoint
              const response = await authenticatedFetch(
                `${config.BACKEND_API_ADDRESS}/work/work-record/reset-work-record-data`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    record_ids: resetOptions.recordIds,
                    reset_all_workflows: resetOptions.resetAllWorkflows
                  })
                }
              );

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const data = await response.json();
              
              alert(
                `Work Record Data Reset Complete!\n\n` +
                `Records processed: ${resetOptions.recordIds.length}\n` +
                `Workflow scope: ${resetOptions.resetAllWorkflows ? 'All workflows by UPC' : 'Current workflows only'}\n\n` +
                `Cleared: Brands, Lenses, Logos, Frame bindings, Final images\n\n` +
                `${data.message || 'Success'}`
              );
            }
            
            closeDialog();
            setSelectedRecords(new Set());
            handleRefresh();
            
          } catch (err) {
            console.error('Error resetting records:', err);
            alert(`Failed to reset records: ${err.message}`);
          }
        }}
        onCancel={closeDialog}
      />
    );
  };

  // Image preview handlers
  const openImagePreview = (url, record) => {
    setImagePreview({
      isOpen: true,
      url,
      record
    });
  };

  const closeImagePreview = () => {
    setImagePreview({
      isOpen: false,
      url: null,
      record: null
    });
  };

  // Fetch available batches
  const fetchBatches = useCallback(async () => {
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/work/work-batches/list`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAvailableBatches(data.batches || []);
    } catch (err) {
      console.error('Error fetching batches:', err);
    }
  }, [authenticatedFetch]);

  // Fetch batches on mount
  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  // Fetch records
  const fetchRecords = useCallback(async (page, resetData = false) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value.toString());
        }
      });

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/work/work-record/list?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (resetData || page === 1) {
        setRecords(data.records);
      } else {
        setRecords(prev => [...prev, ...data.records]);
      }
      
      setTotalCount(data.pagination.total_records);
      setHasMore(data.pagination.has_next_page);
      setCurrentPage(page);
      setInitialLoading(false);
      
    } catch (err) {
      console.error('Error fetching records:', err);
      setError(err.message);
      setInitialLoading(false);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, filters, sortBy, sortOrder]);

  // Fetch unassigned data
  const fetchUnassignedData = async (type) => {
    try {
      const endpoints = {
        brand: '/work/work-record/unassigned-brands',
        lenses: '/work/work-record/unassigned-lenses',
        logo: '/work/work-record/unassigned-logos'
      };

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}${endpoints[type]}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        grouped_records: data.grouped_records,
        available_items: type === 'brand' ? data.available_brands : 
                        type === 'lenses' ? data.available_lenses : 
                        data.available_logos,
        summary: data.summary
      };
    } catch (err) {
      console.error(`Error fetching unassigned ${type}:`, err);
      alert(`Failed to load unassigned ${type} data`);
      return null;
    }
  };

  // Open assignment dialog
  const openAssignmentDialog = async (type) => {
    const data = await fetchUnassignedData(type);
    if (data) {
      setAssignmentDialog({
        type,
        isOpen: true,
        data
      });
    }
  };

  // Close assignment dialog
  const closeAssignmentDialog = () => {
    setAssignmentDialog({
      type: null,
      isOpen: false,
      data: null
    });
  };

  // Handle assignment
  const handleAssign = async (assignments) => {
    const { type } = assignmentDialog;
    
    try {
      const endpoints = {
        brand: '/work/work-record/assign-brand',
        lenses: '/work/work-record/assign-lenses',
        logo: '/work/work-record/assign-logo'
      };

      const idField = type === 'brand' ? 'glasses_brand_id' :
                     type === 'lenses' ? 'glasses_lenses_id' :
                     'glasses_logo_id';

      // Process each assignment
      for (const [groupKey, itemId] of Object.entries(assignments)) {
        const recordsInGroup = assignmentDialog.data.grouped_records[groupKey];
        const recordIds = recordsInGroup.map(r => r.id);

        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}${endpoints[type]}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              record_ids: recordIds,
              [idField]: itemId
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to assign ${type}`);
        }
      }

      alert(`Successfully assigned ${Object.keys(assignments).length} groups!`);
      handleRefresh();
    } catch (err) {
      console.error('Error assigning:', err);
      alert(`Failed to assign ${type}: ${err.message}`);
    }
  };

  // Handle create new
  const handleCreateNew = async (formData, groupKey) => {
    const { type } = assignmentDialog;
    
    try {
      const endpoints = {
        brand: '/work/glasses-brand/create',
        lenses: '/work/glasses-lenses/create',
        logo: '/work/glasses-logo/create'
      };

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}${endpoints[type]}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create ${type}`);
      }

      const data = await response.json();
      const createdItem = data.brand || data.lenses || data.logo;
      
      // Update available items in dialog
      setAssignmentDialog(prev => ({
        ...prev,
        data: {
          ...prev.data,
          available_items: [...prev.data.available_items, createdItem]
        }
      }));

      alert(`Successfully created new ${type}!`);
      return createdItem;
    } catch (err) {
      console.error('Error creating:', err);
      alert(`Failed to create ${type}: ${err.message}`);
      return null;
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    setSelectedRecords(new Set());
    setCurrentPage(1);
    setHasMore(true);
    fetchRecords(1, true);
  }, [filters, sortBy, sortOrder]);

  // Infinite scroll
  useEffect(() => {
    const currentTarget = observerTarget.current;
    
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchRecords(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, currentPage]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      upc: '',
      model: '',
      product: '',
      brand: '',
      lens_color: '',
      output_naming: '',
      needs_logo: null,
      work_batch_id: '',
      has_final_image_id: null,
      has_glasses_frame_id: null,
      has_glasses_frame_image_id: null
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '' && v !== null);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleRowClick = (recordId, index, event) => {
    if (event.ctrlKey || event.metaKey) {
      setSelectedRecords(prev => {
        const next = new Set(prev);
        if (next.has(recordId)) {
          next.delete(recordId);
        } else {
          next.add(recordId);
        }
        return next;
      });
      setLastSelectedIndex(index);
    } else if (event.shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const recordsInRange = records.slice(start, end + 1).map(r => r.id);
      
      setSelectedRecords(prev => {
        const next = new Set(prev);
        recordsInRange.forEach(id => next.add(id));
        return next;
      });
    } else {
      setSelectedRecords(new Set([recordId]));
      setLastSelectedIndex(index);
    }
  };

  const handleSelectAll = () => {
    if (selectedRecords.size === records.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(records.map(r => r.id)));
    }
  };

  const getSelectionIcon = () => {
    if (selectedRecords.size === 0) {
      return <Square className="w-5 h-5" />;
    } else if (selectedRecords.size === records.length) {
      return <CheckSquare className="w-5 h-5" />;
    } else {
      return <MinusSquare className="w-5 h-5" />;
    }
  };

  const handleRefresh = () => {
    setSelectedRecords(new Set());
    setCurrentPage(1);
    setHasMore(true);
    fetchRecords(1, true);
  };

  const handleAssignGlassesFrames = async () => {
    const confirmed = window.confirm(
      'Bind glasses frames to work records?\n\n' +
      'This will:\n' +
      '1. Find work records without a linked frame\n' +
      '2. Match them to a glasses_frame whose file name is the record UPC\n' +
      '3. If the UPC matches nothing, try the record MODEL the same way\n' +
      '4. Link the frame and copy its final image\n\n' +
      'Matching ignores the file extension, case and leading zeros.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/work/work-record/assign-glasses-frames`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const unmatched = (data.unmatched_samples || [])
        .map(u => `  · upc "${u.upc || '—'}" / model "${u.model || '—'}"`)
        .join('\n');

      alert(
        `Frame Binding Complete!\n\n` +
        `Total records processed: ${data.total_records}\n` +
        `Frames found & linked: ${data.frames_linked}\n` +
        `  matched by UPC: ${data.matched_by_upc}\n` +
        `  matched by model: ${data.matched_by_model}\n` +
        `Linked with final image: ${data.frames_linked_with_image}\n` +
        `Linked without image: ${data.frames_linked_without_image}\n` +
        `No matching frame found: ${data.no_frame_found}` +
        (unmatched ? `\n\nUnmatched examples:\n${unmatched}` : '') +
        (data.errors_count > 0 ? `\n\nErrors: ${data.errors_count}` : '')
      );
      
      handleRefresh();
      
    } catch (err) {
      console.error('Error binding glasses frames:', err);
      alert(`Failed to bind glasses frames: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Run the creation. workBatchId null means every batch, which walks the whole
  // table -- that is how records withdrawn from production got their workflows
  // recreated, so the picker defaults to a single batch instead.
  const runWorkflowCreation = async (workBatchId, batchLabel) => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/work/work-record/create-workflows`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ work_batch_id: workBatchId || null }),
        }
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      let message = `Workflow Creation Complete!\n\n`;
      message += `Scope: ${workBatchId ? batchLabel : 'ALL batches'}\n`;
      message += `Total records processed: ${data.total_records}\n`;
      message += `Workflows created: ${data.workflows_created}\n`;
      message += `Workflows skipped (already exist): ${data.workflows_skipped}\n`;

      if (data.errors_count > 0) {
        message += `Errors: ${data.errors_count}\n\nFirst few errors:\n`;
        data.errors.slice(0, 3).forEach(err => {
          message += `- UPC ${err.upc || 'unknown'}: ${err.error}\n`;
        });
        if (data.errors.length > 3) {
          message += `... and ${data.errors.length - 3} more errors\n`;
        }
      }
      alert(message);
      handleRefresh();
    } catch (err) {
      console.error('Error creating workflows:', err);
      alert(`Failed to create workflows: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowCreation = () => {
    openDialog(
      'Create workflows',
      <CreateWorkflowsContent
        authenticatedFetch={authenticatedFetch}
        onConfirm={async (workBatchId, batchLabel) => {
          closeDialog();
          await runWorkflowCreation(workBatchId, batchLabel);
        }}
        onCancel={closeDialog}
      />
    );
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Image Preview Modal */}
      {imagePreview.isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={closeImagePreview}
        >
          <div className="relative max-w-6xl max-h-[90vh] bg-white rounded-lg shadow-2xl overflow-hidden">
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={closeImagePreview}
                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-4">
              {imagePreview.record && (
                <div className="mb-4 text-sm text-gray-600">
                  <p className="font-semibold text-gray-900 mb-1">
                    {imagePreview.record.output_naming}
                  </p>
                  <p>UPC: {imagePreview.record.upc}</p>
                  <p>Brand: {imagePreview.record.brand} | Lens: {imagePreview.record.lens_color}</p>
                </div>
              )}
              <img
                src={imagePreview.url}
                alt="Preview"
                className="max-w-full max-h-[calc(90vh-150px)] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}

      {/* Generic Dialog */}
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
      >
        {dialog.content}
      </Dialog>

      {/* Assignment Dialog */}
      <AssignmentDialog
        isOpen={assignmentDialog.isOpen}
        onClose={closeAssignmentDialog}
        type={assignmentDialog.type}
        data={assignmentDialog.data}
        onAssign={handleAssign}
        onCreateNew={handleCreateNew}
      />

      {/* Main Fixed Navbar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left side - Title and stats */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Work Records</h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalCount.toLocaleString()} total records
              </p>
            </div>

            {/* Right side - Action buttons */}
            <div className="flex items-center gap-3">
              {/* Assignment Buttons Group */}
              <div className="flex items-center gap-2 pr-3 border-r border-gray-300">
                <button
                  onClick={() => openAssignmentDialog('brand')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium border border-indigo-200"
                >
                  <Tag className="w-4 h-4" />
                  Assign Brands
                </button>
                <button
                  onClick={() => openAssignmentDialog('lenses')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium border border-purple-200"
                >
                  <Eye className="w-4 h-4" />
                  Assign Lenses
                </button>
                <button
                  onClick={() => openAssignmentDialog('logo')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm font-medium border border-amber-200"
                >
                  <Award className="w-4 h-4" />
                  Assign Logos
                </button>
              </div>

              {/* Processing Buttons Group */}
              <div className="flex items-center gap-2 pr-3 border-r border-gray-300">
                <button
                  onClick={handleAssignGlassesFrames}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ImageIcon className="w-4 h-4" />
                  Bind to Frames
                </button>
                <button
                  onClick={handleWorkflowCreation}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Create Workflows
                </button>
              </div>

              {/* Utility Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
                    showFilters || hasActiveFilters
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {hasActiveFilters && (
                    <span className="ml-1 px-1.5 py-0.5 bg-indigo-600 text-white text-xs rounded-full">
                      {Object.values(filters).filter(v => v !== '' && v !== null).length}
                    </span>
                  )}
                </button>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-semibold text-red-800">Error loading records</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">
            {loading && !initialLoading && records.length > 0 && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 pointer-events-none">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-lg">
                  <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                  <span className="text-sm text-gray-600 font-medium">Updating...</span>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  {/* Filter Row */}
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-2">{/* Status */}</th>
                    <th className="px-4 py-2">{/* Select */}</th>
                    <th className="px-4 py-2">
                      <select
                        value={filters.has_final_image_id === null ? '' : filters.has_final_image_id.toString()}
                        onChange={(e) => handleFilterChange('has_final_image_id', e.target.value === '' ? null : e.target.value === 'true')}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">All</option>
                        <option value="true">Has Image</option>
                        <option value="false">No Image</option>
                      </select>
                    </th>
                    <th className="px-4 py-2">
                      <select
                        value={filters.work_batch_id || ''}
                        onChange={(e) => handleFilterChange('work_batch_id', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">All Batches</option>
                        {availableBatches.map(batch => (
                          <option key={batch.id} value={batch.id}>
                            {batch.batch_name}
                          </option>
                        ))}
                      </select>
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={filters.upc}
                        onChange={(e) => handleFilterChange('upc', e.target.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={filters.model}
                        onChange={(e) => handleFilterChange('model', e.target.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={filters.product}
                        onChange={(e) => handleFilterChange('product', e.target.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={filters.brand}
                        onChange={(e) => handleFilterChange('brand', e.target.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={filters.lens_color}
                        onChange={(e) => handleFilterChange('lens_color', e.target.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={filters.output_naming}
                        onChange={(e) => handleFilterChange('output_naming', e.target.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <select
                        value={filters.needs_logo === null ? '' : filters.needs_logo.toString()}
                        onChange={(e) => handleFilterChange('needs_logo', e.target.value === '' ? null : e.target.value === 'true')}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      >
                        <option value="">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </th>
                    <th className="px-4 py-2">
                      <select
                        value={filters.has_glasses_frame_id === null ? '' : filters.has_glasses_frame_id.toString()}
                        onChange={(e) => handleFilterChange('has_glasses_frame_id', e.target.value === '' ? null : e.target.value === 'true')}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">All</option>
                        <option value="true">Has Frame</option>
                        <option value="false">No Frame</option>
                      </select>
                    </th>
                    <th className="px-4 py-2">
                      <select
                        value={filters.has_glasses_frame_image_id === null ? '' : filters.has_glasses_frame_image_id.toString()}
                        onChange={(e) => handleFilterChange('has_glasses_frame_image_id', e.target.value === '' ? null : e.target.value === 'true')}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">All</option>
                        <option value="true">Has Image</option>
                        <option value="false">No Image</option>
                      </select>
                    </th>
                    <th className="px-4 py-2">{/* Created */}</th>
                    <th className="px-4 py-2">{/* Actions */}</th>
                  </tr>
                  {/* Header Row */}
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center">
                      <button
                        onClick={handleSelectAll}
                        className="text-gray-600 hover:text-gray-900 mx-auto"
                      >
                        {getSelectionIcon()}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Final Image
                    </th>
                    <th 
                      className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('work_batch_id')}
                    >
                      Batch Name {getSortIndicator('work_batch_id')}
                    </th>
                    <th onClick={() => handleSort('upc')} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      UPC {getSortIndicator('upc')}
                    </th>
                    <th onClick={() => handleSort('model')} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      Model {getSortIndicator('model')}
                    </th>
                    <th onClick={() => handleSort('product')} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      Product {getSortIndicator('product')}
                    </th>
                    <th onClick={() => handleSort('brand')} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      Brand {getSortIndicator('brand')}
                    </th>
                    <th onClick={() => handleSort('lens_color')} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      Lens Color {getSortIndicator('lens_color')}
                    </th>
                    <th onClick={() => handleSort('output_naming')} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      Output Naming {getSortIndicator('output_naming')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Needs Logo
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Frame ID
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Frame Image
                    </th>
                    <th onClick={() => handleSort('created_at')} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      Created {getSortIndicator('created_at')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {records.length > 0 ? (
                    records.map((record, index) => {
                      const isSelected = selectedRecords.has(record.id);
                      const hasBrandId = !!record.glasses_brand_id;
                      const hasLensesId = !!record.glasses_lenses_id;
                      const hasLogoId = !!record.glasses_logo_id;
                      const hasFrameId = !!record.glasses_frame_id;
                      const hasFrameImageId = !!record.glasses_frame_image_id;
                      const hasFinalImageId = !!record.final_image_id;
                      const logoRequired = record.needs_logo === true;
                      const isIncomplete = !hasBrandId || !hasLensesId || (logoRequired && !hasLogoId);
                      const isComplete = hasBrandId && hasLensesId && (!logoRequired || hasLogoId);
                      const hasStatus = record.status !== null && record.status !== undefined;
                      
                      return (
                        <tr
                          key={record.id}
                          className={`cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-indigo-50 hover:bg-indigo-100'
                              : hasFinalImageId
                              ? 'bg-green-50 hover:bg-green-100'
                              : isIncomplete
                              ? 'bg-red-50 hover:bg-red-100'
                              : isComplete
                              ? 'bg-yellow-50 hover:bg-yellow-100'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-center">
                              <div className={`w-3 h-3 rounded-full ${
                                hasStatus ? 'bg-green-500' : 'bg-red-500'
                              }`} title={hasStatus ? 'Status available' : 'No status'}></div>
                            </div>
                          </td>
                          
                          <td className="px-4 py-3 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            <div className="flex justify-center text-indigo-600">
                              {isSelected ? (
                                <CheckSquare className="w-5 h-5" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </td>
                          
                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            {record.final_image_url ? (
                              <button
                                onClick={() => openImagePreview(record.final_image_url, record)}
                                className="inline-block rounded-lg overflow-hidden border-2 border-gray-300 hover:border-indigo-500 transition-colors"
                              >
                                <img
                                  src={record.final_image_url}
                                  alt={record.output_naming}
                                  className="w-16 h-16 object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div class="w-16 h-16 bg-red-100 flex items-center justify-center"><span class="text-red-600 text-xs">Error</span></div>';
                                  }}
                                />
                              </button>
                            ) : (
                              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto">
                                <ImageIcon className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </td>
                          
                          <td className="px-4 py-3 text-sm text-gray-900 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            {record.batch_name ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                {record.batch_name}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          
                          <td className="px-4 py-3 text-sm text-gray-900 font-mono text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            {record.upc}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            {record.model || <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            {record.product || <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            {hasBrandId ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                {record.brand}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-900">{record.brand}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            {hasLensesId ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                {record.lens_color}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-900">{record.lens_color}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-mono text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            {record.output_naming}
                          </td>
                          <td className="px-4 py-3 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            <div className="flex justify-center items-center gap-2">
                              {record.needs_logo ? (
                                <>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Yes
                                  </span>
                                  {hasLogoId && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                      Logo Set
                                    </span>
                                  )}
                                  {record.logo_info && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                      {record.logo_info}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  No
                                </span>
                              )}
                            </div>
                          </td>
                          
                          <td className="px-4 py-3 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            <div className="flex justify-center">
                              {hasFrameId ? (
                                <CheckSquare className="w-5 h-5 text-green-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </td>
                          
                          <td className="px-4 py-3 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            <div className="flex justify-center">
                              {hasFrameImageId ? (
                                <CheckSquare className="w-5 h-5 text-green-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </td>
                          
                          <td className="px-4 py-3 text-sm text-gray-600 text-center" onClick={(e) => handleRowClick(record.id, index, e)}>
                            {new Date(record.created_at).toLocaleDateString()}
                          </td>
                          
                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => console.log('View details:', record.id)}
                              className="px-3 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded transition-colors"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : !loading && !initialLoading ? (
                    <tr>
                      <td colSpan="15" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {hasActiveFilters ? 'No matching records found' : 'No records found'}
                          </h3>
                          <p className="text-gray-600">
                            {hasActiveFilters ? 'Try adjusting your filters' : 'Start by adding some work records'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : initialLoading ? (
                    <tr>
                      <td colSpan="15" className="px-4 py-12 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
                          <span className="text-gray-600 font-medium">Loading records...</span>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {records.length > 0 && (
              <div ref={observerTarget} className="px-6 py-4 text-center border-t border-gray-200">
                {loading && !initialLoading && (
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                    <span className="text-sm text-gray-600">Loading more...</span>
                  </div>
                )}
                {!hasMore && (
                  <span className="text-sm text-gray-500">No more records to load</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Selection Navbar */}
      {selectedRecords.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 shadow-2xl border-t-2 border-indigo-400 z-40">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5" />
                <span className="text-sm font-semibold">
                  {selectedRecords.size} record{selectedRecords.size !== 1 ? 's' : ''} selected
                </span>
              </div>
              <button
                onClick={() => setSelectedRecords(new Set())}
                className="text-sm text-indigo-200 hover:text-white transition-colors flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={handleBulkDelete}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium shadow-lg"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
              <button 
                onClick={handleBulkReset}
                className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium shadow-lg"
              >
                <RotateCcw className="w-4 h-4" />
                Reset Processing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkRecordsTable;